import moment from "moment";
import { AssetMainType, IAssetInfo } from "../models/asset";
import { isTesouroReserva } from "./treasure-reserva";

export class AssetSummary {
    constructor(
        public readonly id: string,
        public readonly assetId: string,
        public readonly totalValue: number,
        public readonly name: string,
        public readonly isPre: boolean|undefined,
        public readonly indexCorrelationName: string|undefined,
        public readonly indexCorrelation: number|undefined,
        public readonly issuingBankName: string,
        public readonly negotiatingBankName: string|undefined,
        public readonly dueDate: Date|undefined,
        public readonly purchaseDate: Date,
        public readonly brokerageValue: number|undefined,
        public readonly othersValue: number|undefined,
        public readonly monthlyCosts?: number,
        public readonly quantity?: number,
        public readonly yearTax?: number,
        public readonly cotation?: number,
        public readonly recurringValue?: number,
        public readonly pbType?: string,
        public readonly type?: AssetMainType,
        public readonly fundType?: "B3" | "CVM",
        public readonly treasureId?: string,
        public readonly isTesouroReserva?: boolean
    ) { }
}

/** Títulos em carteira: acumulado do evento mais recente; sem eventos, a quantidade da compra. */
const treasureHeldQuantity = (response: IAssetInfo): number | undefined => {
    const last = [...(response.asset_event_user ?? [])]
        .sort((left, right) => new Date(left.event_date).getTime() - new Date(right.event_date).getTime())
        .at(-1);
    return last?.event_quantity_amount ?? response.asset_tb_details?.tb_pu_ratio_vna;
};

export class AssetSummaryFactory {
    public static createFromDetailsResponse(response: IAssetInfo) {
        switch (response.asset_type.toUpperCase()) {
            case "CS":
                return new AssetSummary(
                    response.id,
                    response.asset_id,
                    response.asset_current_value,
                    `Personalizado - ${response.asset_alias} - ${response.asset_fin_issue?.inst_name ?? response.asset_fin_negotiating?.inst_name}`,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_fin_issue?.inst_name,
                    response.asset_fin_negotiating?.inst_name,
                    undefined,
                    moment(response.asset_purchase_date).toDate(),
                    response.asset_cs_details?.cs_oc_brokerage,
                    response.asset_cs_details?.cs_oc_others,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_type?.toLowerCase() as AssetMainType
                );
            case "TB":
                return new AssetSummary(
                    response.id,
                    response.asset_id,
                    response.asset_current_value,
                    `${response.asset_tb_details!.treasure.TrsrBd.nm!} - ${response.asset_fin_issue?.inst_name ?? response.asset_fin_negotiating?.inst_name}`,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_fin_issue?.inst_name,
                    response.asset_fin_negotiating?.inst_name,
                    undefined,
                    moment(response.asset_purchase_date).toDate(),
                    response.asset_tb_details?.tb_oc_brokerage,
                    response.asset_tb_details?.tb_oc_others,
                    undefined,
                    isTesouroReserva(response.asset_tb_details?.treasure)
                        ? undefined
                        : treasureHeldQuantity(response),
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_type?.toLowerCase() as AssetMainType,
                    undefined,
                    response.asset_tb_details?.tb_id,
                    isTesouroReserva(response.asset_tb_details?.treasure)
                );
            case "PB":
                return new AssetSummary(
                    response.id,
                    response.asset_id,
                    response.asset_current_value,
                    `${response.asset_pb_details?.pb_type} - ${response.asset_fin_issue?.inst_name ?? response.asset_fin_negotiating?.inst_name}`,
                    !!response.asset_pb_details.pb_rate_pre_annual,
                    response.asset_sub_type,
                    response.asset_pb_details.pb_rate_cdi
                        ?? response.asset_pb_details.pb_rate_pos_plus_cdi
                        ?? response.asset_pb_details.pb_rate_pos_plus_igpm
                        ?? response.asset_pb_details.pb_rate_pos_plus_ipca
                        ?? response.asset_pb_details.pb_rate_pre_annual,
                    response.asset_fin_issue?.inst_name,
                    response.asset_fin_negotiating?.inst_name,
                    moment(response.asset_due_date).toDate(),
                    moment(response.asset_purchase_date).toDate(),
                    response.asset_pb_details?.pb_oc_brokerage,
                    response.asset_pb_details?.pb_oc_others,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_pb_details?.pb_type,
                    response.asset_type?.toLowerCase() as AssetMainType
                );           
            case "CA":
                return new AssetSummary(
                    response.id,
                    response.asset_id,
                    response.asset_current_value,
                    `Conta Corrente - ${response.asset_fin_issue?.inst_name ?? response.asset_fin_negotiating?.inst_name}`,
                    undefined,
                    response.asset_ca_details?.ca_rate_cdi ? "CDI" : "",
                    response.asset_ca_details?.ca_rate_cdi,
                    response.asset_fin_issue?.inst_name,
                    undefined,
                    undefined,
                    moment(response.asset_purchase_date).toDate(),
                    undefined,
                    undefined,
                    response.asset_ca_details.ca_monthly_costs,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_type?.toLowerCase() as AssetMainType
                );
            case "DB":
                return new AssetSummary(
                    response.id,
                    response.asset_id,
                    response.asset_current_value,
                    `Debênture - ${response.asset_db_details.debenture.deb_code} - ${response.asset_fin_issue?.inst_name ?? response.asset_fin_negotiating?.inst_name}`,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_fin_issue?.inst_name,
                    undefined,
                    moment(response.asset_due_date).toDate(),
                    moment(response.asset_purchase_date).toDate(),
                    response.asset_db_details?.db_oc_brokerage,
                    response.asset_db_details?.db_oc_others,
                    response.asset_db_details?.db_quantity_prch,
                    response.asset_db_details?.db_rate_anual,
                    response.asset_db_details?.db_pu_value_prch,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_type?.toLowerCase() as AssetMainType
                );
            case "SA":
                return new AssetSummary(
                    response.id,
                    response.asset_id,
                    response.asset_current_value,
                    `Poupança - ${response.asset_fin_issue?.inst_name ?? response.asset_fin_negotiating?.inst_name}`,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_fin_issue?.inst_name,
                    undefined,
                    undefined,
                    moment(response.asset_purchase_date).toDate(),
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_event_user[0].event_recurring 
                        ? response.asset_event_user[0].event_recurring_value 
                        : undefined,
                    undefined,
                    response.asset_type?.toLowerCase() as AssetMainType
                );
            case "CY":
                return new AssetSummary(
                    response.id,
                    response.asset_id,
                    response.asset_current_value,
                    `${response.asset_cy_details.currency_symbol.name} - ${response.asset_fin_issue?.inst_name ?? response.asset_fin_negotiating?.inst_name}`,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_fin_issue?.inst_name,
                    response.asset_fin_negotiating?.inst_name,
                    response.asset_due_date ? moment(response.asset_due_date).toDate() : undefined,
                    moment(response.asset_purchase_date).toDate(),
                    response.asset_cy_details?.cy_oc_brokerage,
                    response.asset_cy_details?.cy_oc_others,
                    undefined,
                    response.asset_cy_details?.cy_quantity_prch,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_type?.toLowerCase() as AssetMainType
                );
            case "CM":
                return new AssetSummary(
                    response.id,
                    response.asset_id,
                    response.asset_current_value,
                    `${response.asset_cm_details.cm_symbol_from} - ${response.asset_fin_issue?.inst_name ?? response.asset_fin_negotiating?.inst_name}`,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_fin_issue?.inst_name,
                    response.asset_fin_negotiating?.inst_name,
                    response.asset_due_date ? moment(response.asset_due_date).toDate() : undefined,
                    moment(response.asset_purchase_date).toDate(),
                    response.asset_cm_details?.cm_oc_brokerage,
                    response.asset_cm_details?.cm_oc_others,
                    undefined,
                    response.asset_cm_details?.cm_quantity_prch,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_type?.toLowerCase() as AssetMainType
                );
            case "IF":
                return new AssetSummary(
                    response.id,
                    response.asset_id,
                    response.asset_current_value,
                    `${response.asset_if_details.if_ticker_symbol} - ${response.asset_fin_issue?.inst_name ?? response.asset_fin_negotiating?.inst_name}`,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_fin_issue?.inst_name,
                    response.asset_fin_negotiating?.inst_name,
                    response.asset_due_date ? moment(response.asset_due_date).toDate() : undefined,
                    moment(response.asset_purchase_date).toDate(),
                    response.asset_if_details?.if_oc_brokerage,
                    response.asset_if_details?.if_oc_others,
                    undefined,
                    response.asset_if_details?.if_quantity_actual_amount ?? response.asset_if_details?.if_qtd_prch,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_type?.toLowerCase() as AssetMainType,
                    response.asset_if_details.inv_fund?.fund_type ?? "B3"
                );
            case "ST":
                return new AssetSummary(
                    response.id,
                    response.asset_id,
                    response.asset_current_value,
                    `${response.asset_st_details.st_ticker_symbol} - ${response.asset_fin_issue?.inst_name ?? response.asset_fin_negotiating?.inst_name}`,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_fin_issue?.inst_name,
                    response.asset_fin_negotiating?.inst_name,
                    response.asset_due_date ? moment(response.asset_due_date).toDate() : undefined,
                    moment(response.asset_purchase_date).toDate(),
                    response.asset_st_details?.st_oc_brokerage,
                    response.asset_st_details?.st_oc_others,
                    undefined,
                    response.asset_st_details?.st_quantity_actual_amount ?? response.asset_st_details?.st_qtd_prch,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    response.asset_type?.toLowerCase() as AssetMainType
                );
            default:
                throw new Error("Type");
        }
    }
}
